import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { DATA_KEY_PROMO } from '@/data/constants/hdmDataKey';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { getSettings, Settings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { CombinablePromotion } from '@/data/types/Promotions';
import { getUser } from '@/data/User-Server';
import {
	getPhysicalStoreId,
	getStoreDataByPhysicalStoreId,
} from '@/data/utils/commonIntegrationUtils';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { customPromotionDetails } from 'integration/generated/custom';
import { ComHdmCommercePromoPromotionFetch } from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';

export type PromoIconResponse = Omit<ComHdmCommercePromoPromotionFetch, 'promotionData'> & {
	promotionData?: {
		baseQty?: number;
		giftQty?: number;
		relatedCatentries: string[] | undefined;
		physicalStores: string[] | undefined;
		field1: string | undefined;
		giftCatentries: string[] | undefined;
		active: boolean | undefined;
		PromotionName: string | undefined;
	} | null;
	combinablePromotions?: CombinablePromotion[] | null;
};

// declare fetcher
export const promotionDetailsFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			catentryId: string;
			currentPhysicalStoreId: string;
			currentMarketId: string;
			profileName: 'HDM_findDisplayPromotionByCatentryId';
			langId: string;
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams
	) => {
		const response = (await customPromotionDetails(pub).promotionFetchByCatentryIdDetail(
			storeId,
			{
				...query,
				responseFormat: 'json' as const,
			},
			params
		)) as PromoIconResponse;
		return await response;
	};
export type PromoDetailsRequest = {
	catentryId: string;
	physicalStoreId: string;
	marketId: string;
};

export const buildPromoMessageKey = ({
	catentryId,
	storeId,
	currentPhysicalStoreId,
	currentMarketId,
	langId,
}: {
	storeId: string;
	catentryId: string;
	currentPhysicalStoreId: string;
	currentMarketId: string;
	langId: string;
}) => [
	shrink({
		storeId,
		catentryId,
		currentPhysicalStoreId,
		currentMarketId,
		profileName: 'HDM_findDisplayPromotionByCatentryId',
		langId,
		responseFormat: 'json',
	}),
	DATA_KEY_PROMO,
];

//  Declear server side function to get promo message
export const getPromoMessage = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	catentryId: string
): Promise<PromoIconResponse> => {
	const settings = await getSettings(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);
	const user = await getUser(cache, context);
	const physicalStoreId = getPhysicalStoreId(context);
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const query = {
		catentryId,
		currentPhysicalStoreId: physicalStoreId,
		currentMarketId: marketId,
		profileName: 'HDM_findDisplayPromotionByCatentryId',
		langId,
		responseFormat: 'json',
	} as const;
	const props = { storeId, ...query };
	const key = unstableSerialize([shrink(props), DATA_KEY_PROMO]);
	const params = {};

	const cacheScope = getServerCacheScope(context, user.context);

	const value =
		(cache.get(key, cacheScope) as Promise<PromoIconResponse | undefined>) ||
		promotionDetailsFetcher(false)(storeId, query, params);
	const response = await value;
	response && cache.set(key, response, cacheScope);
	return response ?? ({} as PromoIconResponse);
};

//  Declear client side hook to get promo message
export const usePromoMessage = (catentryId: string) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { physicalStoreId } = useDefaultStore();
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const query = {
		catentryId,
		currentPhysicalStoreId: physicalStoreId,
		currentMarketId: marketId,
		profileName: 'HDM_findDisplayPromotionByCatentryId',
		langId,
		responseFormat: 'json',
	} as const;
	const props = { storeId, ...query };
	const { data, error, isLoading, isValidating } = useSWR(
		storeId && catentryId ? buildPromoMessageKey(props) : null,
		async ([]) => await promotionDetailsFetcher(true)(storeId, query, params),
		{ use: [laggyMiddleWare] }
	);
	return {
		data,
		loading: !error && isLoading,
		isValidating,
		error,
	};
};

/**
 * Helper function to fetch promoMessage, only when
 * STOERCONF.ENABLE_API_CALL_FETCH_PROMO is set to true.
 * @param cache
 * @param context
 * @param settings
 * @param productId
 */
export const fetchPromoMessage = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	settings: Settings,
	productId: string
) => {
	const isFetchPromoEnabled = settings?.userData.ENABLE_API_CALL_FETCH_PROMO;
	isFetchPromoEnabled === 'true' && (await getPromoMessage(cache, context, productId));
};
